/** A simple text file to store all the text for the stakeholders module */

import {
  IconClient as IconClientRaw,
  IconOrganization as IconOrganizationRaw,
  IconPlus as IconPlusRaw,
  IconTrash as IconTrashRaw,
  IconVendor as IconVendorRaw,
  type IconComponent
} from '@gat-ship/icons'

const IconClient: IconComponent = IconClientRaw
const IconOrganization: IconComponent = IconOrganizationRaw
const IconPlus: IconComponent = IconPlusRaw
const IconTrash: IconComponent = IconTrashRaw
const IconVendor: IconComponent = IconVendorRaw
import type { PageHeaderProps } from '@gat-ship/vue-components'
type ModalText = {
  title: string
  description: string | ((...args: string[]) => string)
}
import type { StakeholderType } from '@gat-ship/types'

function iconForStakeholderType(stakeholderType: StakeholderType) {
  if (stakeholderType === 'client') {
    return IconClient
  }
  if (stakeholderType === 'vendor') {
    return IconVendor
  }
  return IconOrganization
}

function getStakeholderTypeText(stakeholderType: StakeholderType) {
  if (stakeholderType === 'client') {
    return 'Client'
  }
  if (stakeholderType === 'vendor') {
    return 'Vendor'
  }
  return 'Other Party'
}

export const text = {
  views: {
    /** The header for the list view */
    list: (stakeholderType: StakeholderType) => {
      const buttons = {
        add: {
          label: `Add ${getStakeholderTypeText(stakeholderType)}`,
          icon: IconPlus,
          size: 'default'
        }
      }

      const getDescription = () => {
        if (stakeholderType === 'client') {
          return `Parties responsible for the vessel and commercial operation.`
        }
        if (stakeholderType === 'vendor') {
          return `Operational service providers supporting port calls.`
        }
        return `Operational stakeholders involved for coordination or compliance.`
      }

      const pageHeader = {
        title: getStakeholderTypeText(stakeholderType),
        description: getDescription(),
        icon: iconForStakeholderType(stakeholderType)
      } satisfies PageHeaderProps

      return {
        pageHeader,
        buttons
      }
    },
    createNew: (stakeholderType: StakeholderType) => {
      const getCreateNewDescription = () => {
        if (stakeholderType === 'client') {
          return 'Add a client, such as a shipping company or charterer, that engages your agency for port calls.'
        }
        if (stakeholderType === 'vendor') {
          return 'Add a vendor, such as a shipping company or charterer, that engages your agency for port calls.'
        }
        return 'Add an other party, such as a shipping company or charterer, that engages your agency for port calls.'
      }
      return {
        pageHeader: {
          title: `Add ${getStakeholderTypeText(stakeholderType)}`,
          description: getCreateNewDescription(),
          icon: iconForStakeholderType(stakeholderType)
        } satisfies PageHeaderProps
      }
    },
    contacts: {
      pageHeader: {
        title: 'Contacts',
        description: (stakeholderName: string) =>
          `Contact persons and contact points for ${stakeholderName}`
      }
    }
  },
  /** The groups for the stakeholder layout */
  groups: {
    /** The general information group for all stakeholders */
    generalInformation: (_stakeholderType: StakeholderType) => {
      return {
        title: 'General Information',
        description: '',
        modals: {
          editGeneral: {
            title: 'General Information',
            description: (otherPartyName: string) => otherPartyName
          } satisfies ModalText
        },
        buttons: {
          editGeneral: {
            label: 'Edit',
            variant: 'outline',
            icon: false,
            size: 'sm',
            class: 'w-fit'
          }
        }
      }
    },
    officialBusinessNumbers: (stakeholderType: StakeholderType) => {
      return {
        title: 'Official Business Numbers',
        description:
          stakeholderType === 'client'
            ? "Legally registered identifiers for the client's business."
            : "Legally registered identifiers for the vendor's business.",
        modals: {
          editOfficialBusinessNumbers: {
            title: 'Official Business Numbers',
            description: (stakeholderName: string) => stakeholderName
          } satisfies ModalText
        },
        buttons: {
          editOfficialBusinessNumbers: {
            label: 'Edit',
            variant: 'outline',
            icon: false,
            size: 'sm',
            class: 'w-fit'
          }
        }
      }
    },
    internalIdentificationNumbers: (_stakeholderType: StakeholderType) => {
      return {
        title: 'Internal Identification Numbers',
        description:
          'System-assigned identifiers used for internal reference and integrations.',
        modals: {
          editInternalIdentificationNumbers: {
            title: 'Internal Identification Numbers',
            description: (stakeholderName: string) => stakeholderName
          } satisfies ModalText
        },
        buttons: {
          editInternalIdentificationNumbers: {
            label: 'Edit',
            variant: 'outline',
            icon: false,
            size: 'sm',
            class: 'w-fit'
          }
        }
      }
    },
    address: {
      title: 'Addresses',
      description: ''
    },
    postalAddress: (_stakeholderType: StakeholderType) => {
      return {
        title: 'Postal Address',
        description: 'The official mailing address.',
        modals: {
          changePostalAddress: {
            title: 'Postal Address',
            description: (stakeholderName: string) => stakeholderName
          } satisfies ModalText
        },
        confirmDialogs: {
          removeAddress: {
            title: 'Remove Address',
            message: (stakeholderName: string) =>
              `Are you sure you want to remove the postal address of ${stakeholderName}?`
          }
        },
        buttons: {
          changePostalAddress: {
            label: 'Edit',
            variant: 'outline',
            size: 'sm',
            icon: false
          },
          removeAddress: {
            label: 'Remove Address',
            variant: 'outline-destructive',
            size: 'sm',
            icon: IconTrash
          }
        }
      }
    },
    visitAddress: (_stakeholderType: StakeholderType) => {
      return {
        title: 'Visiting Address',
        description: 'The physical location for visits and meetings.',
        modals: {
          changeVisitAddress: {
            title: 'Visiting Address',
            description: (stakeholderName: string) => stakeholderName
          } satisfies ModalText
        },
        confirmDialogs: {
          removeAddress: {
            title: 'Remove Address',
            message: (stakeholderName: string) =>
              `Are you sure you want to remove the visiting address of ${stakeholderName}?`
          }
        },
        buttons: {
          changeVisitAddress: {
            label: 'Edit',
            variant: 'outline',
            size: 'sm',
            icon: false,
            class: 'w-fit'
          },
          removeAddress: {
            label: 'Remove Address',
            variant: 'outline-destructive',
            icon: IconTrash
          }
        }
      }
    },
    billingAddress: (_stakeholderType: StakeholderType) => {
      return {
        title: 'Billing Address',
        description: 'The official billing address.',
        buttons: {
          changeBillingAddress: {
            label: 'Edit',
            variant: 'outline',
            size: 'sm',
            class: 'w-fit',
            icon: false
          }
        },
        modals: {
          changeBillingAddress: {
            title: 'Change Billing Address',
            description: (clientName: string) => clientName
          } satisfies ModalText
        },
        confirmDialogs: {
          removeAddress: {
            title: 'Remove Address',
            message: (clientName: string) =>
              `Are you sure you want to remove the billing address of ${clientName}?`
          }
        }
      }
    },
    billingDetails: (_stakeholderType: StakeholderType) => {
      return {
        title: 'Billing Details',
        description:
          'All billing details and payment terms for this stakeholder.',
        buttons: {
          updateBillingDetails: {
            label: 'Edit',
            variant: 'outline',
            size: 'sm',
            class: 'w-fit',
            icon: false
          }
        },
        modals: {
          updateBillingDetails: {
            title: 'Billing Details',
            description: (stakeholderName: string) => stakeholderName
          } satisfies ModalText
        }
      }
    },
    billingInformation: (_stakeholderType: StakeholderType) => {
      return {
        title: 'Billing Information',
        description:
          'All billing details and payment terms for this stakeholder.'
      }
    },
    customFields: () => {
      return {
        title: 'Custom Fields',
        description:
          'Additional information fields configured for this stakeholder type.',
        buttons: {
          editCustomFields: {
            label: 'Edit',
            variant: 'outline',
            icon: false,
            size: 'sm',
            class: 'w-fit'
          }
        }
      }
    },
    removeStakeholder: (stakeholderType: StakeholderType) => {
      return {
        title: `Remove Stakeholder`,
        description:
          'Removing a stakeholder makes it unavailable for future use but does not delete historical data.',
        buttons: {
          removeStakeholder: {
            label: `Remove ${getStakeholderTypeText(stakeholderType)}`,
            variant: 'outline',
            icon: false,
            class: 'w-fit'
          }
        },
        confirmDialog: {
          title: `Remove ${getStakeholderTypeText(stakeholderType)}`,
          message: (stakeholderName: string) =>
            `The stakeholder will no longer appear in selections or searches, and existing references are preserved for audit and reporting purposes.

          Are you sure you want to remove ${stakeholderName}?`
        }
      }
    },
    restoreStakeholder: (stakeholderType: StakeholderType) => {
      return {
        title: `Restore ${getStakeholderTypeText(stakeholderType)}`,
        description: 'Restore this stakeholder to your agency.',
        buttons: {
          restoreStakeholder: {
            label: `Restore ${getStakeholderTypeText(stakeholderType)}`,
            variant: 'outline',
            icon: false,
            class: 'w-fit'
          }
        },
        confirmDialog: {
          title: `Restore ${getStakeholderTypeText(stakeholderType)}`,
          message: (stakeholderName: string) =>
            `Are you sure you want to restore ${stakeholderName}?`
        }
      }
    },
    contactPersons: {
      title: 'Contacts',
      description: ''
    }
  },
  contactPersons: {
    deleteContactPerson: {
      title: 'Delete Contact Person',
      message: (contactPersonName: string) =>
        `Are you sure you want to delete the contact person ${contactPersonName}?`
    }
  }
} as const
